import { Autocomplete, TextField } from '@mui/material';
import { useAuthStore, type WorkspaceRef } from '../stores/auth.store';

/** The shared workspace picker. `multiple` switches the value between a single id and an id array. */
export function WorkspaceSelect(props: {
  multiple?: boolean;
  value: string | string[];
  onChange: (v: string | string[]) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  hideWhenSingle?: boolean;
  disabled?: boolean;
  options?: WorkspaceRef[];
  sx?: object;
  /** Marks the field required and hints while empty; the form blocks submit itself. */
  required?: boolean;
}) {
  const fromStore = useAuthStore((s) => s.user?.workspaces ?? []);
  const options = props.options ?? fromStore;
  const size = props.size ?? 'small';

  if (props.hideWhenSingle && options.length <= 1) return null;

  const byId = new Map(options.map((o) => [o.id, o]));

  if (props.multiple) {
    const value = (props.value as string[]).map((id) => byId.get(id)).filter(Boolean) as WorkspaceRef[];
    return (
      <Autocomplete
        multiple
        size={size}
        options={options}
        value={value}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, v) => props.onChange(v.map((o) => o.id))}
        sx={props.sx}
        renderInput={(p) => (
          <TextField {...p} label={props.label} placeholder={value.length ? undefined : props.placeholder} />
        )}
      />
    );
  }

  const single = byId.get(props.value as string) ?? null;
  return (
    <Autocomplete
      size={size}
      options={options}
      value={single}
      disabled={props.disabled}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => props.onChange(v?.id ?? '')}
      sx={props.sx}
      renderInput={(p) => (
        <TextField {...p} label={props.label ?? 'Workspace'} required={props.required}
          helperText={props.required && !single ? 'Choose the workspace this belongs to' : undefined} />
      )}
    />
  );
}

/** No preselection (#297): the user's only workspace, else '' so the form must ask. */
export function defaultCreateWorkspace(): string {
  const all = useAuthStore.getState().user?.workspaces ?? [];
  return all.length === 1 ? all[0].id : '';
}

export function WorkspaceField({ value, onChange, disabled }: {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const options = useAuthStore((s) => s.user?.workspaces ?? []);
  if (options.length <= 1) return null;
  return <WorkspaceSelect value={value} onChange={(v) => onChange(v as string)} disabled={disabled} required />;
}
